import {Component, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute} from '@angular/router'
import {ApiVirtualizationLayerService} from '../../services/api-virtualization-layer/api-virtualization-layer.service'
import {Person} from '../../models/person'
import {Employee} from '../../models/employee'
import {debounceTime, switchMap, tap} from 'rxjs/operators'
import {VineaLocation} from '../../models/vinea-location'
import {NgbDateStruct} from '@ng-bootstrap/ng-bootstrap'
import {Group} from '../../models/group'
import Relator from '../../helpers/relator/relator'
import {Builder} from '../../models/builder'
import {Sort} from '../../models/filter/sort'
import {Location} from '@angular/common';
import {of, Subject, Subscription} from 'rxjs';

@Component({
  selector:    'ngx-mass-messenger-location-filter-form',
  templateUrl: './mass-messenger-location-filter-form.component.html',
  styleUrls:   ['./mass-messenger-location-filter-form.component.scss']
})
export class MassMessengerLocationFilterFormComponent implements OnInit, OnDestroy {

  private sub;
  private id;

  public loading          = true;
  public locationsLoading = true;
  public groupsLoading    = true;

  public employee: Employee;
  public person: Person;
  public locations: Array<VineaLocation | any> = [{id: 0, name: 'Elke locatie'}];
  public groups: Array<Group>                  = [];
  public groupsCount: number;
  public selectedGroup: Group                  = new Group();
  public page                                  = 1;

  // Entities and Filters
  public entitiesQueryBuilder                      = new Builder();
  public childEntities                             = [];
  public entitiesSearch$: Subject<boolean>         = new Subject();
  public ageMin: number                            = 0;
  public ageMax: number                            = 0;
  public entitiesPage: number                      = 1;
  public searchEntityName: string                  = '';
  public entitiesLoading: boolean                  = true;
  private entitiesSearchSubscription: Subscription = new Subscription();

  // Group Filters
  public groupQueryBuilder              = new Builder();
  public searchGroupName                = '';
  public selectedLocation               = {id: 0, name: 'Elke locatie'};
  public searchGroupDate: NgbDateStruct = {day: 0, month: 0, year: 0};
  public searchPresent                  = false;
  public groupsSearch$: Subject<any>    = new Subject();
  public subscription: Subscription     = new Subscription();

  // Subscriptions
  private subLocations$ = new Subscription();

  constructor(
    private route: ActivatedRoute,
    private apiv: ApiVirtualizationLayerService,
    public location: Location,
  ) {
  }

  ngOnInit() {
    const vm                              = this;
    this.groupQueryBuilder.params['page'] = this.page;
    this.groupQueryBuilder.sort           = new Sort('name', 'asc');

    this.sub = this.route.params.subscribe(() => {
      const b = new Builder;
      b.sort  = new Sort('name', 'asc');

      this.loading = false;

      // Show only on active date
      this.activeOnly();

      this.subLocations$ = this.apiv.getAllLocations$(b).pipe(
        // Get all locations
        tap(locations => {
          Relator.mergeOnce(locations, this.locations);
          this.locations        = [...this.locations];
          this.locationsLoading = false;
        }),
      ).subscribe(() => {
        // Refilter Groups
        vm.setupGroupFilters();
        vm.groupsSearch$.next(true);
        vm.reFilterEntities();
      });
    });
  }

  ngOnDestroy() {
    this.sub.unsubscribe();
    this.subscription.unsubscribe();
    this.entitiesSearchSubscription.unsubscribe();
    this.subLocations$.unsubscribe();
  }

  setupGroupFilters() {
    // Filter the groups
    this.subscription = this.groupsSearch$.pipe(
      debounceTime(800),
      // distinctUntilChanged(),
      tap((reset) => {
        if (reset) {
          this.page   = 1;
          this.groups = [];
          this.groups = [...this.groups];
        }

        this.groupQueryBuilder.params['filter[name]'] = this.searchGroupName;
        if (this.searchPresent) {
          this.groupQueryBuilder.params['state'] = 'start';
        }else {
          delete this.groupQueryBuilder.params['state'];
        }
        if (this.selectedLocation && this.selectedLocation.id) {
          this.groupQueryBuilder.params['filter[location_id]'] = this.selectedLocation.id;
        }

        if (this.searchGroupDate && this.searchGroupDate['day']) {
          this.groupQueryBuilder.params['filter[ends_at_or_after]'] = this.formatDateStruct(this.searchGroupDate);
        }

        this.groupQueryBuilder.params['page'] = this.page;
        this.groupsLoading                    = true;
      }),
      switchMap(() => this.apiv.getGroups(this.groupQueryBuilder)),
      tap(groups => {
        if (groups) {
          Relator.mergeOnce(groups['data'], this.groups);
          this.groups      = [...this.groups];
          this.groupsCount = groups['meta']['total'];
          this.page++;
          this.groupsLoading  = false;
          this.groupsQuerying = false;
        }
      }),
    ).subscribe();
  }

  activeOnly() {
    const date           = new Date();
    this.searchGroupDate = {
      day:   date.getDate(),
      month: date.getMonth() + 1,
      year:  date.getFullYear()
    };
  }

  private groupsQuerying = false;

  getGroups(reset = false) {
    if (reset) {
      this.page   = 1;
      this.groups = [];
    }
    this.groupQueryBuilder.params['page'] = this.page;
    this.groupsLoading                    = true;

    if (!this.groupsQuerying) {
      this.groupsQuerying = true;
      return this.apiv.getGroups(this.groupQueryBuilder).pipe(tap(groups => {
        if (reset) {
          this.groups = [];
        }
        Relator.mergeOnce(groups['data'], this.groups);
        this.groups      = [...this.groups];
        this.groupsCount = groups['meta']['total'];
        this.page++;
        this.groupsLoading  = false;
        this.groupsQuerying = false;
      }));
    } else {
      return of({});
    }
  }

  getLocation(loc_id) {
    const loc = Relator.findKeyById(loc_id, this.locations);
    if (loc !== -1) {
      return this.locations[loc];
    } else {
      return {name: ''}
    }
  }

  onGroupsScrollDown() {
    this.groupsSearch$.next(false);
  }

  reFilterGroups() {
    this.groupsSearch$.next(true);
    // this.groupQueryBuilder.params['filter[name]'] = this.searchGroupName;
    // if (this.searchLocationId && this.searchLocationId.id) {
    //   this.groupQueryBuilder.params['filter[location_id]'] = this.searchLocationId.id;
    // }
    //
    // if (this.searchGroupDate && this.searchGroupDate['day']) {
    //   this.groupQueryBuilder.params['filter[ends_at_or_after]'] = this.formatDateStruct(this.searchGroupDate);
    // }
    //
    // return this.getGroups(true).subscribe();
  }

  reFilterEntities() {
//    /**
//     * The entities can be filtered by:
//     * - All (when no Location is chosen)
//     * - Location (when no Group is chosen)
//     * - Group
//     *
//     * - Age Min
//     * - Age Max
//     */
//    if (this.ageMin && this.ageMax) {
//      this.entitiesQueryBuilder.params['filter[age]'] = [this.ageMin, this.ageMax];
//    } else {
//      this.entitiesQueryBuilder.params['filter[age]'] = null;
//    }
//
//    this.entitiesQueryBuilder.params['name'] = this.searchEntityName;
//    this.entitiesQueryBuilder = this.entitiesQueryBuilder;
  }

  private formatDateStruct(d: NgbDateStruct) {
    return d.year + '-' + d.month + '-' + d.day;
  }

  select(group) {
    this.selectedGroup = group;
  }

  clearSelection() {
    this.selectedGroup = new Group();
  }

  selected(group) {
    return this.selectedGroup.id === group.id;
  }

}
